import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AnimatedContentLoader,
  Button,
  FileAnalysisBadges,
  FilePreview,
  FileThumbnail,
  FilterBar,
  SearchInput,
  TextTooltip,
  UploadDropzone,
  UploadFilesButton
} from '@components'
import { useVirtualList } from '@hooks'
import { useAllowUpload } from '@hooks/useAllowUpload.tsx'
import {
  collectFromPages,
  fetchFiles,
  fetchFolder,
  fetchFolders,
  IFile,
  ISubFolder,
  uploadFile,
  UploadFileVariables,
  UploadOptions
} from '@query'
import { useScreen } from '@utils'
import { getAttachmentNoteTooltipContent, hasAttachmentNote } from '@utils/attachmentNotes'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheckCircle, FaCircle, FaCloudUploadAlt, FaStickyNote } from 'react-icons/fa'
import { FaMagnifyingGlassPlus } from 'react-icons/fa6'
import styled from 'styled-components'
import BreadCrumbs from './BreadCrumbs'
import Folder from './Folder'

const ITEM_HEIGHT = 46
const FILE_PREVIEW_BUTTON_CLASS = 'preview-button'
const FILE_NOTE_BUTTON_CLASS = 'note-button'

type SelectorItem = { type: 'folder'; item: ISubFolder } | { type: 'file'; item: IFile }

type FileFilters = Record<string, unknown> | null

interface FileSelectorProps {
  slug: string
  isDisabled?: boolean
  initialSelections?: number[]
  onClose: (ids: number[]) => void
}

export const FileSelector: React.FC<FileSelectorProps> = ({
  slug,
  isDisabled = false,
  initialSelections = [],
  onClose
}) => {
  const [t] = useTranslation()
  const [selectedFiles, setSelectedFiles] = useState<number[]>(initialSelections)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<FileFilters>(null)
  const [folderId, setFolderId] = useState<number>()
  const queryClient = useQueryClient()
  const allowUpload = useAllowUpload()
  const { isMobile } = useScreen()

  const fetchFoldersForView = useCallback(
    async (activeFolderId?: number) => {
      if (activeFolderId) {
        const data = await fetchFolder({ slug, folderId: activeFolderId })
        return { folders: data.children, breadcrumbs: data.breadcrumbs }
      }

      const data = await fetchFolders({ slug }, { page_size: 9999 })
      return { folders: data.results, breadcrumbs: [] }
    },
    [slug]
  )

  const hasSearch = Boolean(search)
  const hasFilters = Boolean(filters && Object.keys(filters).length > 0)
  const hideFolders = hasSearch || hasFilters

  const { data: dataFolders, isLoading: isFoldersLoading } = useQuery({
    queryKey: [slug, 'folders', folderId],
    queryFn: () => fetchFoldersForView(folderId),
    ...{
      enabled: !hideFolders
    }
  })

  const folders = dataFolders?.folders
  const breadcrumbs = dataFolders?.breadcrumbs || []

  const queryParams = useMemo(
    () => ({
      search: search || null,
      folder: hasSearch ? null : folderId,
      root: hideFolders ? null : !folderId,
      ...(filters || {})
    }),
    [search, hasSearch, folderId, hideFolders, filters]
  )

  const {
    data: fileData,
    isLoading: isFilesLoading,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: [slug, 'files', queryParams],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => fetchFiles({ slug }, { page: pageParam, ...queryParams }),
    getNextPageParam: lastPage => lastPage.next,
    getPreviousPageParam: lastPage => lastPage.prev
  })
  const files = collectFromPages<IFile>(fileData)

  const items = useMemo<SelectorItem[]>(
    () => [
      ...(hideFolders
        ? []
        : folders?.map(item => ({
            type: 'folder' as const,
            item
          })) || []),
      ...(files?.map(item => ({
        type: 'file' as const,
        item
      })) || [])
    ],
    [hideFolders, folders, files]
  )

  const uploadMutation = useMutation<IFile, unknown, UploadFileVariables>({
    mutationFn: ({ file, ...options }) =>
      uploadFile(file, { slug, fileName: file.name, folderId }, options),
    ...{
      onSuccess: async function (file) {
        await queryClient.invalidateQueries({ queryKey: [slug, 'files'] })
        setSelectedFiles(selectedFiles => {
          const newFiles = [...selectedFiles, file.id]
          return newFiles
        })
      }
    }
  })
  const uploadFileCallback = useCallback(
    (file: File, options?: UploadOptions) => uploadMutation.mutateAsync({ file, ...options }),
    [uploadMutation]
  )

  const toggleSelection = useCallback((selectedId: number) => {
    setSelectedFiles(previousSelections =>
      previousSelections.includes(selectedId)
        ? previousSelections.filter(fileId => fileId !== selectedId)
        : [...previousSelections, selectedId]
    )
  }, [])

  const stopEventPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation()
  }, [])

  const stopPreviewClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      stopEventPropagation(event)
    },
    [stopEventPropagation]
  )

  const hasActionButtonClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const eventTarget = event.target as Node | null
    if (!eventTarget || !event.currentTarget.contains(eventTarget)) return false
    if (event.defaultPrevented) return false
    const eventPath = event.nativeEvent.composedPath?.() || []
    return eventPath.some(
      node =>
        node instanceof HTMLElement &&
        (node.classList.contains(FILE_PREVIEW_BUTTON_CLASS) ||
          node.classList.contains(FILE_NOTE_BUTTON_CLASS))
    )
  }, [])

  const { renderItems, scrollRef } = useVirtualList({
    items,
    itemSize: ITEM_HEIGHT,
    overshootCount: 10,
    onScrollBottom: () => hasNextPage && fetchNextPage()
  })

  const handleOnSelect = useCallback(() => {
    onClose(selectedFiles)
  }, [onClose, selectedFiles])

  const renderFolderItem = useCallback(
    (folder: ISubFolder, style: React.CSSProperties) => (
      <Folder
        style={style}
        key={`folder-${folder.id}`}
        folder={folder}
        onClick={nextFolderId => setFolderId(nextFolderId)}
      />
    ),
    []
  )

  const renderFileItem = useCallback(
    (file: IFile, style: React.CSSProperties) => (
      <SelectableFile
        key={`file-${file.id}`}
        style={style}
        onClick={event => {
          if (hasActionButtonClick(event)) return
          toggleSelection(file.id)
        }}
      >
        <Column $width={24}>
          <AnimatePresence mode="wait">
            {selectedFiles.includes(file.id) ? (
              <IconWrapper key="selected">
                <FaCheckCircle size={16} className="icon selected" />
              </IconWrapper>
            ) : (
              <IconWrapper key="not-selected">
                <FaCircle size={16} className="icon" />
              </IconWrapper>
            )}
          </AnimatePresence>
        </Column>

        <Column>
          <FileThumbnail
            size={24}
            filetype={file.type}
            thumb={file.file}
            blurhash={file.blurhash}
          />
        </Column>

        <Column $grow>
          <FileName>{file.name}</FileName>
          <FileAnalysisBadges badges={file?.analysis_badges} fileId={file.id} />
        </Column>

        <RowActions>
          {hasAttachmentNote(file.notes) && (
            <TextTooltip
              tooltip={getAttachmentNoteTooltipContent(file.notes, t('files.notes.empty'))}
              maxWidth={360}
              placement="left"
            >
              <RowActionButton
                type="button"
                className={FILE_NOTE_BUTTON_CLASS}
                aria-label={t('files.notes.label')}
                onClick={stopPreviewClick}
                onMouseDown={stopEventPropagation}
                onPointerDown={stopEventPropagation}
                onKeyDown={stopEventPropagation}
              >
                <FaStickyNote size={18} />
              </RowActionButton>
            </TextTooltip>
          )}

          <FilePreview fileId={file.id}>
            <RowActionButton
              className={FILE_PREVIEW_BUTTON_CLASS}
              type="button"
              onClick={stopPreviewClick}
              onMouseDown={stopEventPropagation}
              onPointerDown={stopEventPropagation}
              onKeyDown={stopEventPropagation}
              aria-label="Preview file"
            >
              <FaMagnifyingGlassPlus size={18} />
            </RowActionButton>
          </FilePreview>
        </RowActions>
      </SelectableFile>
    ),
    [
      hasActionButtonClick,
      selectedFiles,
      stopEventPropagation,
      stopPreviewClick,
      t,
      toggleSelection
    ]
  )

  return (
    <StyledFileSelector>
      <PaddedContainer>
        <SearchInput
          autoFocus={!isMobile}
          placeholder={t('components.fileSelector.searchPlaceholder')}
          onSearch={searchTerm => setSearch(searchTerm)}
        />
      </PaddedContainer>

      <FilterContainer>
        <FilterBar
          onFilter={filter => setFilters(filter || null)}
          filters={[
            {
              id: 'not-used',
              name: t('files.filters.notUsed'),
              filter: { is_used: false }
            }
          ]}
        />
      </FilterContainer>

      <BreadCrumbs
        breadcrumbs={breadcrumbs}
        searchValue={search}
        onClick={(folderId: number) => setFolderId(folderId)}
      />

      <FileList ref={scrollRef}>
        <UploadDropzone
          uploadFile={uploadFileCallback}
          disabled={!allowUpload}
          enableAttachmentNotes
        >
          <AnimatedContentLoader
            isLoading={isFilesLoading || isFoldersLoading}
            isEmpty={items.length === 0}
            isEmptyDescription={t('components.fileSelector.searchNotFound')}
          >
            {renderItems((data, style) => {
              if (data.type === 'folder') {
                return renderFolderItem(data.item, style)
              }

              if (data.type === 'file') {
                return renderFileItem(data.item, style)
              }

              return null
            })}
          </AnimatedContentLoader>
        </UploadDropzone>
      </FileList>
      <ButtonContainer>
        <UploadFilesButton
          type="button"
          icon={<FaCloudUploadAlt />}
          uploadFile={uploadFileCallback}
          enableAttachmentNotes
        >
          {t('components.fileSelector.uploadFiles')}
        </UploadFilesButton>

        <Button
          type="button"
          icon={<FaCheckCircle />}
          intent="primary"
          onClick={handleOnSelect}
          disabled={isDisabled || uploadMutation.isPending}
        >
          <ButtonContent>
            <ButtonText>{t('components.fileSelector.done')}</ButtonText>
            <ButtonCount>{selectedFiles.length}</ButtonCount>
          </ButtonContent>
        </Button>
      </ButtonContainer>
    </StyledFileSelector>
  )
}

const StyledFileSelector = styled.div`
  background: ${({ theme }) => theme.colors.neutralWhite};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const IconWrapper = styled(motion.div).attrs({
  initial: { scale: 0.8, opacity: 0 },
  animate: { scale: 1.0, opacity: 1 },
  exit: { scale: 0.8, opacity: 0 },
  transition: { duration: 0.2 }
})`
  display: flex;

  & > * {
    align-self: center;
  }
`

const FileList = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  overflow: auto;
`

const PaddedContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
`

const FilterContainer = styled.div`
  width: 100%;
`

const ButtonContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`

const SelectableFile = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.md}rem;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  gap: ${({ theme }) => theme.spacing.md}rem;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  .preview-button {
    opacity: 0;
    pointer-events: none;
  }

  &:hover .preview-button,
  &:focus-within .preview-button {
    opacity: 1;
    pointer-events: auto;
  }

  & > * {
    align-self: center;
  }

  .icon {
    fill: ${({ theme }) => theme.colors.neutralGray};

    &.selected {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  flex-shrink: 0;
`

const Column = styled.div<{ $width?: number; $grow?: boolean }>`
  min-width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  ${({ $grow }) => $grow && 'flex: 1;'}
`

const FileName = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const RowActionButton = styled.button`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.metalGray};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0;
  line-height: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: none;
  }

  svg {
    display: block;
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`

const ButtonContent = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ButtonText = styled.span`
  align-self: center;
`

const ButtonCount = styled.span`
  background: white;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: bold;
  padding: 0 0.4rem;
  border-radius: 0.6rem;
  right: 0;
  align-self: center;
`
